'use client';

import { Drawer } from '../../Drawer/Drawer';
import { ASSETS_SELECT_ITEMS_PER_PAGE } from './useAllAssetSelectSearchAndInfiniteScroll';
import { InfiniteScroll } from '@bts-web/design-system/component/infinite-scroll';
import { SkeletonElement } from '../../loading/SkeletonElement/SkeletonElement';
import { TransferEntityPickerEntry } from '../../../../crypto-transfers/components/Reusable/TransferEntityPickerEntry/TransferEntityPickerEntry';
import {
  getPrecisionFloatValue,
  intlFiatValueFormatting,
} from '@bts-web/utils-formatting';
import { useClientLocale, useClientTranslation } from '@bts-web/utils-lokalise';
import { getAppConfig } from '@bts-web/core-app-config';
import { NoResults } from '../../NoResults/NoResults';
import { AssetsListSkeleton } from '../../../../prices/AssetsDiscoverPageSkeleton/AssetsDiscoverPageSkeleton';
import { css } from '@bts-web/utils-style-engine';
import { MAXIMUM_SEARCH_TERM_LENGTH } from '../../../../config';
import { SearchBar } from '../../SearchBar/SearchBar';
import { AssetsDrawerFilterHeader } from './subcomponents/AssetsDrawerFilterHeader/AssetsDrawerFilterHeader';
import { AssetSelectDrawerItemEntry } from './utils';
import { useAssetSelect } from './useAssetSelect';
import { useClientUserInfo } from '../../../userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { IconMapper } from '../../../../trade/ChangeCurrencyOrSwap/changeCurrencyUtils';
import {
  DrawerComponent,
  useDrawerMetadata,
} from '../../ModalDrawerContext/ModalDrawerContext';
import { FilledDismiss, FilledSearch } from '../../../svgIcons';
import { PerformanceDisclaimerBanner } from '../../../../performance-disclaimers/components/PerformanceDisclaimerBanner.use-client';
import { getPerformanceDisclaimerClientTranslations } from '../../../../performance-disclaimers/utils/getClientTranslations';

interface AssetSelectDrawerProps {
  onSelect: (asset: AssetSelectDrawerItemEntry) => void;
  ownedOnly?: boolean;
  hidePillFilters?: boolean;
  showFiatOption?: boolean;
  selectedAssetId?: string;
  keepDrawerOpenAfterSelect?: boolean;
  showDisclaimer?: boolean;
}

const AssetSelectDrawer: DrawerComponent<AssetSelectDrawerProps> = ({
  onSelect,
  ownedOnly = false,
  hidePillFilters = false,
  showFiatOption = false,
  selectedAssetId,
  keepDrawerOpenAfterSelect = false,
  showDisclaimer = false,
}) => {
  const { fiat } = useClientUserInfo();

  const { currency } = getAppConfig();

  const { t } = useClientTranslation();

  const locale = useClientLocale();

  const { closeThisDrawer } = useDrawerMetadata();

  const {
    assetFilterSearchValue,
    assets,
    canLoadMore,
    clearSearchParams,
    isLoading,
    loadMore,
    onInputChange,
    groups,
    setGroups,
    assetGroups,
  } = useAssetSelect({ ownedOnly });

  const onAssetSelect = (asset: AssetSelectDrawerItemEntry) => {
    onSelect(asset);

    if (!keepDrawerOpenAfterSelect) {
      closeThisDrawer();
    }
  };

  const renderAssetValueBasedOnType = (asset: AssetSelectDrawerItemEntry) => {
    if (asset.type === 'owned' && asset.ownedAmountFiat) {
      return intlFiatValueFormatting(asset.ownedAmountFiat?.value.toString(), {
        locale: locale,
        notation: 'standard',
        currency,
        fractionDigits: asset.ownedAmountFiat.precision ?? 2,
      });
    } else if (asset.type === 'all' && asset.value) {
      return intlFiatValueFormatting(asset.value.value.toString(), {
        locale: locale,
        notation: 'standard',
        currency,
        fractionDigits: asset.value.precision,
      });
    }

    return;
  };

  const renderAssetQuantity = (asset: AssetSelectDrawerItemEntry) => {
    if (asset.type === 'owned') {
      return getPrecisionFloatValue(asset.ownedAmountAsset);
    }

    return;
  };

  const renderAssetItem = (item: AssetSelectDrawerItemEntry) => {
    const isFiatOption = item.id === fiat.symbol;

    const isDisabled = item.id === selectedAssetId;

    return (
      <TransferEntityPickerEntry
        withinList
        title={item.name}
        subtitle={item.symbol}
        active={false}
        disabled={isDisabled}
        type="entryWithActiveState"
        {...(isFiatOption
          ? {
              iconElement: IconMapper[fiat.symbol as keyof typeof IconMapper],
            }
          : {
              imageUrl: item.logoUrl,
              endInfoMain: renderAssetValueBasedOnType(item),
              endInfoSub: renderAssetQuantity(item),
            })}
        onPick={() => onAssetSelect(item)}
        key={item.id}
      />
    );
  };

  const itemList = showFiatOption
    ? [
        {
          id: fiat.symbol as string,
          name: fiat.name as string,
          symbol: fiat.symbol as string,
          logoUrl: null,
          type: 'owned',
          ownedAmountFiat: {
            value: '0',
          },
          ownedAmountAsset: {
            value: '0',
          },
        },
        ...assets,
      ]
    : assets;

  return (
    <Drawer>
      <div
        className={css({
          p: 'small',
          display: 'flex',
          flexDirection: 'column',
          gap: 'medium',
        })}
      >
        <SearchBar
          onChange={onInputChange}
          onClear={clearSearchParams}
          placeholder={t('search_assets')}
          maxLength={MAXIMUM_SEARCH_TERM_LENGTH}
          searchIcon={<FilledSearch size={24} />}
          dismissIcon={<FilledDismiss />}
        />

        {hidePillFilters === false &&
        setGroups &&
        !ownedOnly &&
        // do not display the filter categories if there is only one, or you are in the owned assets view since it uses a different query, or they are explicitly hidden by props
        assetGroups.length > 1 ? (
          <AssetsDrawerFilterHeader
            groups={groups}
            setGroups={setGroups}
            assetGroups={assetGroups}
          />
        ) : null}

        {showDisclaimer && (
          <PerformanceDisclaimerBanner
            variant="fixed"
            translations={getPerformanceDisclaimerClientTranslations(t)}
            className={css({ paddingX: 0, paddingY: 0 })}
          />
        )}

        {assets.length === 0 && assetFilterSearchValue.length > 0 ? (
          <NoResults searchTerm={assetFilterSearchValue} />
        ) : assets.length > 0 ? (
          <ul aria-label="asset selection list">
            {ownedOnly ? (
              itemList.map((item) =>
                renderAssetItem(item as AssetSelectDrawerItemEntry),
              )
            ) : (
              <InfiniteScroll
                hasMore={!!canLoadMore}
                itemsPerPage={ASSETS_SELECT_ITEMS_PER_PAGE}
                fetchMore={loadMore || (() => Promise.resolve())}
                skeleton={<SkeletonElement width="100%" height="60px" />}
                items={itemList}
                renderItemComponent={(item) =>
                  renderAssetItem(item as AssetSelectDrawerItemEntry)
                }
              />
            )}
          </ul>
        ) : isLoading ? (
          <AssetsListSkeleton />
        ) : null}
      </div>
    </Drawer>
  );
};

export { AssetSelectDrawer };
